<script setup lang="ts">
import { computed } from 'vue'

export type ChipColor = 'primary' | 'neutral' | 'dark' | 'success' | 'warning' | 'danger'
export type ChipVariant = 'solid' | 'outline'
export type ChipSize = 'small' | 'medium'

interface Props {
  label?: string
  /** Semantic color. */
  color?: ChipColor
  /** Visual appearance — orthogonal to color. */
  variant?: ChipVariant
  size?: ChipSize
  /** PrimeIcons class string (e.g. `"pi pi-check"`). Rendered as inline `<i>` inside the chip. */
  icon?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  color: 'neutral',
  variant: 'solid',
  size: 'medium',
})

const chipClasses = computed(() => [
  'chip',
  `chip--color-${props.color}`,
  `chip--variant-${props.variant}`,
  `chip--size-${props.size}`,
])
</script>

<template>
  <span :class="chipClasses">
    <i v-if="icon" :class="[icon, 'chip__icon']" />
    <slot>{{ label }}</slot>
  </span>
</template>

<style scoped>
.chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  border-radius: 999px;
  line-height: 1.2;
  font-weight: 500;
}

.chip__icon {
  font-size: 0.7em;
}

/* Sizes */
.chip--size-small {
  padding: 0.15rem 0.6rem;
  font-size: 10px;
  line-height: 14px;
  vertical-align: middle;
}

.chip--size-medium {
  padding: 0.4rem 0.8rem;
  font-size: 0.75rem;
}

/* Solid variants — color sets bg + text */
.chip--color-primary.chip--variant-solid {
  background: var(--p-primary-500);
  color: var(--p-primary-contrast-color, #fff);
}

/* Quiet grey badge (design: Gray-100 fill, muted label). Tokens rather than the
   literal #F5F5F5 so dark mode lands on the surface-100 card/chip bg. The
   design draws this one as a soft-cornered rectangle, not a full pill, so it
   overrides the shared 999px radius and sets its own type scale. */
.chip--color-neutral.chip--variant-solid {
  background: var(--p-surface-100, #f5f5f5);
  color: var(--p-text-muted-color, #414651);
  border-radius: var(--p-border-radius-sm);
  padding: 0.1875rem 0.625rem;
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* "dark" is a bespoke decorative slot — deeper than the semantic info
   ramp's info-800. Promote to a kit token when a second surface needs it. */
.chip--color-dark.chip--variant-solid {
  background: #194185;
  color: var(--p-surface-0);
}

.chip--color-success.chip--variant-solid {
  background: var(--p-success-700);
  color: var(--p-surface-0);
}

.chip--color-warning.chip--variant-solid {
  background: var(--p-yellow-600);
  color: var(--p-surface-0);
}

.chip--color-danger.chip--variant-solid {
  background: var(--p-red-500);
  color: var(--p-surface-0);
}

/* Outline variants — color sets text + border (some have tinted bg) */
.chip--color-primary.chip--variant-outline {
  background: transparent;
  color: var(--p-primary-600);
  border: 1px solid var(--p-primary-400);
}

.chip--color-neutral.chip--variant-outline {
  background: transparent;
  color: var(--p-text-color);
  border: 1px solid var(--p-content-border-color);
}

.chip--color-dark.chip--variant-outline {
  background: transparent;
  color: #194185;
  border: 1px solid #194185;
}

.chip--color-success.chip--variant-outline {
  background: var(--p-success-50);
  color: var(--p-success-700);
  border: 1px solid var(--p-success-200);
}

.chip--color-warning.chip--variant-outline {
  background: transparent;
  color: var(--p-yellow-700);
  border: 1px solid var(--p-yellow-400);
}

.chip--color-danger.chip--variant-outline {
  background: transparent;
  color: var(--p-red-600);
  border: 1px solid var(--p-red-400);
}
</style>
